/**
 * useSolutions — ViewModel for /products/solutions. Customer-facing
 * bundles of product variants (migration 0118).
 *
 * Per SOP 02 §"MVVM Pattern" + §"List Pagination (LOCKED)".
 *
 * Operator flow: list / add / edit / delete. Add + edit use multipart
 * (solution JSON part + hero image File part). Selling price is
 * derived server-side from the linked variants' current prices.
 */

import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import { parseApiError } from '../utils/api/parseApiError'
import { mutateJson } from '../utils/api/mutateJson'
import { usePaginatedList } from './usePaginatedList'

export interface SolutionVariantPickerEntry {
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string | null
  readonly selling_price_paisa: number | null
}

export interface SolutionVariantDto {
  readonly variant_id: string
  readonly product_id: string
  readonly product_name: string
  readonly axes_label: string | null
  readonly quantity: number
  readonly sort_order: number
  readonly unit_selling_price_paisa: number | null
  readonly line_total_paisa: number | null
}

export interface SolutionMediaDto {
  readonly link_id: number
  readonly media_id: string
  readonly gateway_path: string
  readonly content_type: string
  readonly size_bytes: number
  readonly width: number | null
  readonly height: number | null
  readonly original_name: string | null
  readonly alt_text: string | null
  readonly sort_order: number
  readonly is_thumbnail: boolean
}

export interface SolutionDto {
  readonly id: string
  readonly name: string
  readonly description: string
  readonly derived_selling_price_paisa: number | null
  readonly active_variant_count: number
  readonly variants: SolutionVariantDto[]
  readonly media: SolutionMediaDto[]
  readonly thumbnail_media_id: string | null
  readonly thumbnail_url: string | null
  readonly created_at: string
  readonly updated_at: string
}

export interface SolutionVariantAssignment {
  readonly variant_id: string
  readonly quantity: number
  readonly sort_order: number
}

export interface CreateSolutionInput {
  readonly name: string
  readonly description: string
  readonly variants: SolutionVariantAssignment[]
  readonly altText: string | null
  readonly imageFile: File
}

export interface UpdateSolutionInput {
  name?: string
  description?: string
  variants?: SolutionVariantAssignment[]
  altText?: string | null
  imageFile?: File
}

interface ListBody {
  readonly success: true
  readonly data: {
    readonly items: SolutionDto[]
    readonly total: number
    readonly page: number
    readonly pageSize: number
  }
}

interface PickerBody {
  readonly success: true
  readonly data: {
    readonly variants: SolutionVariantPickerEntry[]
  }
}

export interface UseSolutionsReturn {
  readonly items: ReadonlyArray<SolutionDto>
  readonly total: number
  readonly page: number
  readonly pageSize: number
  readonly isLoading: boolean
  readonly error: string | null
  readonly setPage: (n: number) => void
  readonly reload: () => void
  readonly variantPicker: ReadonlyArray<SolutionVariantPickerEntry>
  readonly pickerError: string | null
  readonly create: (input: CreateSolutionInput) => Promise<
    { ok: true } | { ok: false; field: string | null; message: string }
  >
  readonly update: (id: string, patch: UpdateSolutionInput) => Promise<
    { ok: true } | { ok: false; field: string | null; message: string }
  >
  readonly remove: (id: string) => Promise<{ ok: true } | { ok: false; message: string }>
}

function buildCreateForm(input: CreateSolutionInput): FormData {
  const form = new FormData()
  form.append(
    'solution',
    JSON.stringify({
      name: input.name,
      description: input.description,
      variants: input.variants,
      altText: input.altText
    })
  )
  form.append('image', input.imageFile, input.imageFile.name)
  return form
}

function buildUpdateForm(input: UpdateSolutionInput): FormData {
  const form = new FormData()
  const part: Record<string, unknown> = {}
  if (input.name !== undefined) part.name = input.name
  if (input.description !== undefined) part.description = input.description
  if (input.variants !== undefined) part.variants = input.variants
  if (input.altText !== undefined) part.altText = input.altText
  if (Object.keys(part).length > 0) form.append('solution', JSON.stringify(part))
  if (input.imageFile !== undefined) form.append('image', input.imageFile, input.imageFile.name)
  return form
}

export function useSolutions(): UseSolutionsReturn {
  const { authedFetch } = useAuth()
  const [variantPicker, setVariantPicker] = useState<SolutionVariantPickerEntry[]>([])
  const [pickerError, setPickerError] = useState<string | null>(null)

  const paged = usePaginatedList<SolutionDto, undefined>({
    fetcher: useCallback(
      async (page, pageSize, signal) => {
        const url = `${APIROUTES.SOLUTIONS}?page=${page}&pageSize=${pageSize}`
        const res = await authedFetch(url, { signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as ListBody
        return { items: body.data.items, total: body.data.total }
      },
      [authedFetch]
    )
  })

  useEffect(() => {
    let cancelled = false
    const ctrl = new AbortController()
    setPickerError(null)
    void (async () => {
      try {
        const res = await authedFetch(APIROUTES.SOLUTIONVARIANTSPICKER, { signal: ctrl.signal })
        if (!res.ok) throw new Error((await parseApiError(res)).message)
        const body = (await res.json()) as PickerBody
        if (!cancelled) setVariantPicker(body.data.variants)
      } catch (err) {
        if (cancelled) return
        if (err instanceof DOMException && err.name === 'AbortError') return
        setPickerError(err instanceof Error ? err.message : 'Failed to load picker')
      }
    })()
    return () => {
      cancelled = true
      ctrl.abort()
    }
  }, [authedFetch])

  const create = useCallback(
    async (input: CreateSolutionInput) => {
      const res = await authedFetch(APIROUTES.SOLUTIONS, {
        method: 'POST',
        body: buildCreateForm(input)
      })
      if (res.ok) {
        paged.reload()
        return { ok: true as const }
      }
      const err = await parseApiError(res)
      return { ok: false as const, field: err.field, message: err.message }
    },
    [authedFetch, paged]
  )

  const update = useCallback(
    async (id: string, patch: UpdateSolutionInput) => {
      const res = await authedFetch(fillPath(APIROUTES.SOLUTIONITEM, { id }), {
        method: 'PATCH',
        body: buildUpdateForm(patch)
      })
      if (res.ok) {
        paged.reload()
        return { ok: true as const }
      }
      const err = await parseApiError(res)
      return { ok: false as const, field: err.field, message: err.message }
    },
    [authedFetch, paged]
  )

  const remove = useCallback(
    async (id: string) => {
      const r = await mutateJson<{ ok: true }>(
        authedFetch,
        fillPath(APIROUTES.SOLUTIONITEM, { id }),
        'DELETE'
      )
      if (r.ok) {
        paged.reload()
        return { ok: true as const }
      }
      return { ok: false as const, message: r.message }
    },
    [authedFetch, paged]
  )

  return {
    items: paged.items,
    total: paged.total,
    page: paged.page,
    pageSize: paged.pageSize,
    isLoading: paged.isLoading,
    error: paged.error,
    setPage: paged.setPage,
    reload: paged.reload,
    variantPicker,
    pickerError,
    create,
    update,
    remove
  }
}
